import { BiddingIntradayOrderStatus } from '@ebalance/proto/bffuser/v1/bidding_common_model_pb'
import { useTranslation } from 'react-i18next'
import { Link as RouterLink, generatePath } from 'react-router'

import {
  Box,
  Link,
  TableBody,
  TableCell,
  TableColumnHeader,
  TableHeader,
  TableRoot,
  TableRow,
  Text,
} from '@enechain/ecloud-designsystem'

import { INTRADAY_STATUS_BADGE } from '~/components/bidding/BiddingIntradayStatusTag'
import {
  type EnumBadgeSpec,
  EnumStatusBadge,
} from '~/components/common/EnumStatusBadge'
import { TradeSideLabel } from '~/components/common/TradeSideLabel'
import type { IntradayOrderRow } from '~/hooks/bidding/useListBiddingIntradayOrders'
import { convertAreaLabel } from '~/models/area'
import { decimalToNumber } from '~/models/decimal'
import {
  KOMA_DIVIDER_ROW_CSS,
  komaListDividerFlags,
} from '~/models/komaDivider'
import { komaNumberToTime } from '~/models/komaToTime'
import { TextOrNoValue } from '~/pages/IntradayBidding/components/IntradayCellValue'
import {
  executionStatus,
  matchedVolumeTotalMw,
  remainingVolumeMw,
} from '~/pages/IntradayBidding/models/executionResult'
import { routePaths } from '~/routes/routePaths'

type Props = {
  rows: IntradayOrderRow[]
}

const DASH = '—'

/**
 * 約定ステータス列 (AC-BIN-022a) の表示仕様。 値は「約定 / 部分約定」 で、
 * ステータス列 (AC-BIN-035 の「全約定」 等) とは別の軸の語彙を持つ
 * (`intraday-bidding.md` 英語付録「約定 | Traded」)。 色はステータス列と揃える。
 */
const EXECUTION_TRADE_STATUS_BADGE: Record<
  BiddingIntradayOrderStatus,
  EnumBadgeSpec
> = {
  ...INTRADAY_STATUS_BADGE,
  [BiddingIntradayOrderStatus.ALL_MATCHED]: {
    ...INTRADAY_STATUS_BADGE[BiddingIntradayOrderStatus.ALL_MATCHED],
    labelKey: 'page:biddingIntraday.executionResult.detail.statusValue.traded',
  },
  [BiddingIntradayOrderStatus.PARTIAL_MATCHED]: {
    ...INTRADAY_STATUS_BADGE[BiddingIntradayOrderStatus.PARTIAL_MATCHED],
    labelKey: 'page:biddingIntraday.executionResult.detail.statusValue.partial',
  },
}
const fmtVol = (v: number | undefined): string =>
  v === undefined ? DASH : v.toFixed(1)
const fmtPrice = (v: number | undefined): string =>
  v === undefined ? DASH : v.toFixed(2)

/**
 * 注文の JEPX 約定番号。 backend が約定日時の昇順で返すので先頭が最も早い約定。
 * 約定番号が台帳に無い行は DASH。 データテキストは行の 14px のまま、 添え書きだけ
 * design どおり小さく中強調にする。
 */
const JepxExecutionNos = ({
  nos,
}: {
  nos: readonly string[]
}): React.ReactNode => {
  const { t } = useTranslation()
  const [first, ...rest] = nos
  if (first === undefined) {
    return DASH
  }
  return (
    <>
      {first}
      {rest.length > 0 && (
        <>
          {' '}
          <Box as="span" color="Text.midEmphasis" fontSize="sm">
            {t(
              'page:biddingIntraday.executionResult.detail.jepxExecutionNoMore',
              {
                count: rest.length,
              },
            )}
          </Box>
        </>
      )}
    </>
  )
}

/**
 * 約定明細テーブル (= design `intraday-result.html` `#intraday-result-body`)。
 *
 * 数量列: 約定量(MW) = 1 コマあたりのスナップショット値 (= 保持値そのまま)、
 * 約定量合計(MW) = コマ別数量の単純合算 (AC-BIN-022a / AC-BIN-043、 2026-09-08 決定 B で
 * 全画面 MW に統一。 MWh へは換算しない)。 1 注文 = 1 コマ の間は 2 列は同じ値になる。
 * 残数量(MW) = backend が返す札の値 (注文量 − 約定量。 札が終わった後は約定しなかった数量、 #8736)。
 * 収支の列は持たない (AC-BIN-079)。
 *
 * 行は **注文単位の集計約定** (= 約定済の注文 1 件 = 1 行)。 JEPX 約定番号の列は、
 * 1 注文に複数の約定があれば約定日時が最も早い約定の番号に「ほか N件」を添える
 * (Des 判断 #9546)。 約定ごとの内訳 (約定日時・約定番号の全件) は行を開いた時に出す
 * AC-BIN-022 の領分で、 この表には持たない。
 */
export const ExecutionDetailTable = ({ rows }: Props): React.ReactElement => {
  const { t } = useTranslation()

  if (rows.length === 0) {
    return (
      <Box padding={4} textAlign="center">
        <Text color="Text.lowEmphasis">
          {t('page:biddingIntraday.executionResult.empty')}
        </Text>
      </Box>
    )
  }

  // 3 時間 (6 コマ) ごとの区切り線 (conventions.md §48 コマの表の区切り線)。 コマが飛ぶ・
  // 1 コマが複数行の一覧なので、 境目をまたぐ行の下に 1 本だけ引く。
  const komaDividerFlags = komaListDividerFlags(rows, (row) => row.timeCode)
  return (
    <Box overflowX="auto">
      <TableRoot fontSize="md" size="sm" variant="line">
        <TableHeader>
          <TableRow>
            <TableColumnHeader>
              {t('page:biddingIntraday.executionResult.detail.timeCode')}
            </TableColumnHeader>
            <TableColumnHeader>
              {t('page:biddingIntraday.executionResult.detail.area')}
            </TableColumnHeader>
            <TableColumnHeader textAlign="center">
              {t('page:biddingIntraday.executionResult.detail.direction')}
            </TableColumnHeader>
            <TableColumnHeader textAlign="end">
              {t('page:biddingIntraday.executionResult.detail.price')}
            </TableColumnHeader>
            <TableColumnHeader textAlign="end">
              {t('page:biddingIntraday.executionResult.detail.volumeMw')}
            </TableColumnHeader>
            <TableColumnHeader textAlign="end">
              {t('page:biddingIntraday.executionResult.detail.volumeTotalMw')}
            </TableColumnHeader>
            <TableColumnHeader textAlign="end">
              {t('page:biddingIntraday.executionResult.detail.remainingVolume')}
            </TableColumnHeader>
            <TableColumnHeader>
              {t('page:biddingIntraday.executionResult.detail.status')}
            </TableColumnHeader>
            <TableColumnHeader>
              {t('page:biddingIntraday.executionResult.detail.jepxExecutionNo')}
            </TableColumnHeader>
            <TableColumnHeader textAlign="end" whiteSpace="nowrap">
              {t('page:biddingIntraday.column.deliveryContractCd')}
            </TableColumnHeader>
            <TableColumnHeader whiteSpace="nowrap">
              {t('page:biddingIntraday.column.workflowId')}
            </TableColumnHeader>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row, rowIndex) => (
            <TableRow
              key={row.id}
              css={
                komaDividerFlags[rowIndex] === true
                  ? KOMA_DIVIDER_ROW_CSS
                  : undefined
              }
            >
              <TableCell>{komaNumberToTime(row.timeCode)}</TableCell>
              <TableCell>{convertAreaLabel(row.areaCode)}</TableCell>
              <TableCell textAlign="center">
                <TradeSideLabel side={row.direction} />
              </TableCell>
              <TableCell textAlign="end">
                {fmtPrice(decimalToNumber(row.matchedPriceYenPerKwh))}
              </TableCell>
              <TableCell textAlign="end">
                {fmtVol(decimalToNumber(row.matchedVolumeMwh))}
              </TableCell>
              <TableCell textAlign="end">
                {fmtVol(matchedVolumeTotalMw(row))}
              </TableCell>
              <TableCell textAlign="end">
                {fmtVol(remainingVolumeMw(row))}
              </TableCell>
              <TableCell>
                <EnumStatusBadge
                  map={EXECUTION_TRADE_STATUS_BADGE}
                  value={executionStatus(row)}
                />
              </TableCell>
              <TableCell whiteSpace="nowrap">
                <JepxExecutionNos nos={row.jepxExecutionNos} />
              </TableCell>
              <TableCell textAlign="end" whiteSpace="nowrap">
                <TextOrNoValue value={row.deliveryContractCd} />
              </TableCell>
              <TableCell whiteSpace="nowrap">
                {row.workflowId && row.workflowCaseId ? (
                  <Link asChild>
                    <RouterLink
                      onClick={(event) => event.stopPropagation()}
                      to={generatePath(routePaths.workflowCaseDetail.path, {
                        caseId: row.workflowCaseId,
                      })}
                    >
                      {row.workflowId}
                    </RouterLink>
                  </Link>
                ) : (
                  <TextOrNoValue value={row.workflowId} />
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </TableRoot>
    </Box>
  )
}
